import * as Tone from 'tone';

export function PlayPauseButton({
  container = document.body,
  audioUrl,
  buttonId = 'play-btn',
  labelPlay = 'Play',
  labelPause = 'Pause',
  buttonClass = '--big',
  onPlay,
  onPause,
  showAudioControlsLabel = false,
} = {}) {
  if (!audioUrl) throw new Error('audioUrl is required');
  
  // Create button element
  const btn = document.createElement('button');
  btn.id = buttonId;
  btn.setAttribute('aria-pressed', 'false');
  btn.setAttribute('aria-label', labelPlay);
  btn.className = buttonClass;
  
  // SVG icons and label
  btn.innerHTML = `
    <span class="playpause-svg" aria-hidden="true">
      <svg width="36" height="24" viewBox="0 0 36 24" fill="currentColor">
        <path class="play-icon" d="M6 3v18l24-9z" style="display:inline;" />
        <g class="pause-icon" style="display:none;">
          <rect x="7" y="3" width="18" height="18" rx="1"/>
        </g>
      </svg>
    </span>
    <span class="play-label">${labelPlay}</span>
  `;

  // Append to container
  container.appendChild(btn);

  // Setup Tone.js Player
  let player = null;
  let buffer = null;
  let isPlaying = false;
  let isPlayRequested = false;
  let hasShownAudioControls = false;
  const playLabel = btn.querySelector('.play-label');
  const playIcon = btn.querySelector('.play-icon');
  const pauseIcon = btn.querySelector('.pause-icon');



  // Brown noise setup
  const brownNoise = new Tone.Noise('brown');
  const brownGain = new Tone.Gain(0).toDestination();
  brownNoise.connect(brownGain);
  let brownNoisePlaying = false;

  // Helper: get the first section.JACKS0N__01
  function getSection() {
    return document.querySelector('section.JACKS0N__01');
  }

  // Helper: fade in brown noise
  function fadeInBrownNoise() {
    try {
      if (!brownNoisePlaying && Tone.context.state === 'running') {
        brownNoise.start();
        brownNoisePlaying = true;
      }
      brownGain.gain.cancelScheduledValues(Tone.now());
      brownGain.gain.setValueAtTime(0, Tone.now());
      brownGain.gain.linearRampToValueAtTime(0.08, Tone.now() + 0.08); // Quiet, fast fade
    } catch (error) {
      console.error('Failed to start brown noise:', error);
    }
  }

  // Helper: fade out and stop brown noise (longer fade for overlap)
  function fadeOutBrownNoise() {
    brownGain.gain.cancelScheduledValues(Tone.now());
    brownGain.gain.linearRampToValueAtTime(0, Tone.now() + 0.6); // Longer fade out for overlap
    setTimeout(() => {
      if (brownNoisePlaying) {
        brownNoise.stop();
        brownNoisePlaying = false;
      }
    }, 700);
  }

  // Load the audio buffer
  buffer = new Tone.Buffer({
    url: audioUrl,
    onload: () => {
      console.log('Audio buffer loaded successfully');
      player = new Tone.Player(buffer).toDestination();
      
      // Set player to loop automatically
      player.loop = true;
      
      console.log('Player created, buffer loaded:', player.buffer.loaded);
      
      if (isPlayRequested) {
        console.log('Play was requested, starting player...');
        if (player && player.buffer && player.buffer.loaded) {
          console.log('Starting main audio player...');
          fadeOutBrownNoise();
          Tone.Transport.start();
          player.start();
          console.log('Main audio player started');
          btn.setAttribute('aria-pressed', 'true');
          btn.setAttribute('aria-label', labelPause);
          playLabel.textContent = labelPause;
          isPlaying = true;
          isPlayRequested = false;
          if (playIcon) playIcon.style.display = 'none';
          if (pauseIcon) pauseIcon.style.display = 'inline';
          // Add .is-playing to section
          const section = getSection();
          if (section) section.classList.add('is-playing');
          
          // Show audio controls label if enabled and not shown before
          if (showAudioControlsLabel && !hasShownAudioControls) {
            console.log('Attempting to show audio controls label (buffer loaded)...');
            const audioControlsLabel = document.querySelector('#audio-controls label');
            console.log('Found audio controls label:', audioControlsLabel);
            if (audioControlsLabel) {
              audioControlsLabel.setAttribute('data-show', 'true');
              hasShownAudioControls = true;
              console.log('Added data-show attribute to label');
              
              setTimeout(() => {
                if (audioControlsLabel) {

                  console.log('Removed data-show attribute from label');
                  audioControlsLabel.setAttribute('data-show', 'false');
                }
              }, 5000);
            }
          }
          
          if (onPlay) onPlay(player);
        } else {
          console.error('Player or buffer not ready:', { player: !!player, buffer: !!player?.buffer, loaded: player?.buffer?.loaded });
        }
      } else {
        console.log('Play not requested yet, player ready for later');
      }
    },
    onerror: (e) => {
      console.error('Error loading audio buffer:', e);
      playLabel.textContent = 'Error loading audio';
      btn.disabled = true;
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'none';
      fadeOutBrownNoise();
    }
  });

  // Toggle play/pause logic, shared by button and .channel
  async function togglePlayPause() {
    // iOS Safari audio context initialization using native Web Audio API
    try {
      // Try native Web Audio API first
      if (!window.audioContext) {
        window.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      if (window.audioContext.state === 'suspended') {
        await window.audioContext.resume();
      }
      
      // Now try Tone.js
      await Tone.start();
      
      // Try to resume if suspended
      if (Tone.context.state === 'suspended') {
        await Tone.context.resume();
      }
      
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
    }
    
    const section = getSection();
    if (!isPlaying) {
      // Add .is-playing immediately
      if (section) section.classList.add('is-playing');
      // Add .is-pressed and update icons immediately
      btn.classList.add('is-pressed');
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'inline';
      // Always play brown noise briefly
      fadeInBrownNoise();
      if (player && player.buffer && player.buffer.loaded) {
        setTimeout(() => {
          fadeOutBrownNoise();
          Tone.Transport.start();
          player.start();
          
          // Show audio controls label if enabled and not shown before
          if (showAudioControlsLabel && !hasShownAudioControls) {
            console.log('Attempting to show audio controls label (immediate play)...');
            const audioControlsLabel = document.querySelector('#audio-controls label');
            console.log('Found audio controls label:', audioControlsLabel);
            if (audioControlsLabel) {
              audioControlsLabel.setAttribute('data-show', 'true');
              hasShownAudioControls = true;
              console.log('Added data-show attribute to label');
              
              setTimeout(() => {
                if (audioControlsLabel) {
                  audioControlsLabel.removeAttribute('data-show');
                  console.log('Removed data-show attribute from label');
                }
              }, 5000);
            }
          }
          
          if (onPlay) onPlay(player);
        }, 320); // Start mp3 after 320ms, but noise fades out over 600ms
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', labelPause);
        playLabel.textContent = labelPause;
        isPlaying = true;
      } else {
        isPlayRequested = true;
        playLabel.textContent = 'Loading...';
        // If not loaded, brown noise will fade out when mp3 is ready
      }
    } else {
      // Remove .is-playing immediately
      if (section) section.classList.remove('is-playing');
      // Remove .is-pressed and update icons immediately
      btn.classList.remove('is-pressed');
      if (playIcon) playIcon.style.display = 'inline';
      if (pauseIcon) pauseIcon.style.display = 'none';
      if (player) {
        player.stop();
        Tone.Transport.stop();
        if (onPause) onPause(player);
      }
      fadeOutBrownNoise();
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', labelPlay);
      playLabel.textContent = labelPlay;
      isPlaying = false;
    }
  }

  btn.addEventListener('click', togglePlayPause);

  // Also allow .channel to toggle play/pause
  const channel = document.querySelector('.channel');
  if (channel) {
    channel.addEventListener('click', togglePlayPause);
  }

  // Ensure initial state is play icon only
  if (playIcon) playIcon.style.display = 'inline';
  if (pauseIcon) pauseIcon.style.display = 'none';

  // Add close button functionality if audio controls label is enabled
  if (showAudioControlsLabel) {
    // Wait for DOM to be ready, then find and wire up the close button
    setTimeout(() => {
      const audioControls = document.querySelector('#audio-controls');
      if (audioControls) {
        const closeButton = audioControls.querySelector('.__close');
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const audioControlsLabel = audioControls.querySelector('label');
            if (audioControlsLabel) {
              audioControlsLabel.setAttribute('data-show', 'false');
            }
          });
        }
      }
    }, 100);
  }

  return btn;
} 